import { MenuItem, PropertyValue, type RadioItem } from '@affine/component';
import type { FilterParams } from '@affine/core/modules/collection-rules';
import { type DocRecord, DocService } from '@affine/core/modules/doc';
import {
  EditorSettingService,
  getDefaultPageWidth,
  isPageWidth,
  type PageWidth,
  resolvePageWidth,
} from '@affine/core/modules/editor-setting';
import { useI18n } from '@affine/i18n';
import { LongerIcon } from '@blocksuite/icons/rc';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useMemo } from 'react';

import { PlainTextDocGroupHeader } from '../explorer/docs-view/group-header';
import { StackProperty } from '../explorer/docs-view/stack-property';
import type { GroupHeaderProps } from '../explorer/types';
import { FilterValueMenu } from '../filter/filter-value-menu';
import type { PropertyValueProps } from '../properties/types';
import { PropertyRadioGroup } from '../properties/widgets/radio-group';
import { container } from './page-width.css';

const usePageWidthLabel = () => {
  const t = useI18n();
  return useCallback(
    (width: PageWidth) =>
      width === 'fullWidth'
        ? t[
            'com.affine.settings.editorSettings.page.default-page-width.full-width'
          ]()
        : width === 'wide'
          ? t[
              'com.affine.settings.editorSettings.page.default-page-width.wide'
            ]()
          : t[
              'com.affine.settings.editorSettings.page.default-page-width.standard'
            ](),
    [t]
  );
};

export const PageWidthValue = ({ readonly }: PropertyValueProps) => {
  const t = useI18n();
  const editorSetting = useService(EditorSettingService).editorSetting;
  const defaultPageWidth = getDefaultPageWidth(
    useLiveData(editorSetting.settings$)
  );

  const doc = useService(DocService).doc;
  const pageWidth = useLiveData(doc.properties$.selector(p => p.pageWidth));

  const radioValue = resolvePageWidth(pageWidth, defaultPageWidth);

  const radioItems = useMemo<RadioItem[]>(
    () => [
      {
        value: 'standard',
        label:
          t[
            'com.affine.settings.editorSettings.page.default-page-width.standard'
          ](),
        testId: 'standard-width-trigger',
      },
      {
        value: 'wide',
        label:
          t[
            'com.affine.settings.editorSettings.page.default-page-width.wide'
          ](),
        testId: 'wide-width-trigger',
      },
      {
        value: 'fullWidth',
        label:
          t[
            'com.affine.settings.editorSettings.page.default-page-width.full-width'
          ](),
        testId: 'full-width-trigger',
      },
    ],
    [t]
  );

  const handleChange = useCallback(
    (value: string) => {
      doc.record.setProperty('pageWidth', value);
    },
    [doc]
  );
  return (
    <PropertyValue className={container} hoverable={false} readonly={readonly}>
      <PropertyRadioGroup
        value={radioValue}
        onChange={handleChange}
        items={radioItems}
        width={280}
        disabled={readonly}
      />
    </PropertyValue>
  );
};

export const PageWidthDocListProperty = ({ doc }: { doc: DocRecord }) => {
  const label = usePageWidthLabel();
  const pageWidth = useLiveData(doc.properties$.selector(p => p.pageWidth));

  return (
    <StackProperty icon={<LongerIcon />}>
      {label(resolvePageWidth(pageWidth, 'standard'))}
    </StackProperty>
  );
};

export const PageWidthFilterValue = ({
  filter,
  isDraft,
  onDraftCompleted,
  onChange,
}: {
  filter: FilterParams;
  isDraft?: boolean;
  onDraftCompleted?: () => void;
  onChange?: (filter: FilterParams) => void;
}) => {
  const label = usePageWidthLabel();
  const selected = resolvePageWidth(filter.value, 'standard');

  return (
    <FilterValueMenu
      isDraft={isDraft}
      onDraftCompleted={onDraftCompleted}
      items={
        <>
          {(['fullWidth', 'wide', 'standard'] as const).map(width => (
            <MenuItem
              key={width}
              onClick={() => {
                onChange?.({
                  ...filter,
                  value: width,
                });
              }}
              selected={selected === width}
            >
              {label(width)}
            </MenuItem>
          ))}
        </>
      }
    >
      <span>{label(selected)}</span>
    </FilterValueMenu>
  );
};

export const PageWidthGroupHeader = ({
  groupId,
  docCount,
}: GroupHeaderProps) => {
  const label = usePageWidthLabel();
  const text = isPageWidth(groupId) ? label(groupId) : 'Default';

  return (
    <PlainTextDocGroupHeader groupId={groupId} docCount={docCount}>
      {text}
    </PlainTextDocGroupHeader>
  );
};
